import { useState, useEffect } from "react";
import swal from "sweetalert";
import Loader from "../Common/Loader";
import { useDispatch, useSelector } from "react-redux";
import { fetchPatientReviewAction } from "../../../store/actions/PatientReviewAction";
import axiosInstance from "../../../services/AxiosInstance";

const PatientReviewList = () => {
  const dispatch = useDispatch();
  const { reviewList, loading } = useSelector((state) => state.patientReview);

  const [dataList, setDataList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;

  useEffect(() => {
    dispatch(fetchPatientReviewAction());
  }, [dispatch]);

  // ✅ Format API data
  useEffect(() => {
    const formatted = (reviewList || []).map((item) => ({
      ...item,
      patient_name: `${item.first_name || ""} ${item.middile_name || ""} ${
        item.last_name || ""
      }`.trim(),
    }));

    setDataList(formatted);
  }, [reviewList]);

  // ✅ APPROVE
  const handleApprove = (item) => {
    swal({
      title: "Approve?",
      text: `Approve entry for ${item.patient_name}?`,
      icon: "info",
      buttons: true,
    }).then(async (yes) => {
      if (!yes) return;

      try {
        await axiosInstance.post("patient/adminReviewApproval", {
          patient_id: item.patient_id,
          review_id: item.review_id,
        });

        swal("Approved!", "", "success");

        // 🔥 Optimistic UI update
        setDataList((prev) =>
          prev.map((row) =>
            row.review_id === item.review_id
              ? { ...row, admin_approval: "Approved" }
              : row
          )
        );

        dispatch(fetchPatientReviewAction());
      } catch {
        swal("Error", "Approval failed", "error");
      }
    });
  };

  // ✅ SEARCH + SORT
  const filteredData = dataList
    ?.filter((obj) =>
      obj.patient_name?.toLowerCase().includes(searchText.toLowerCase())
    )
    .sort((a, b) => {
      const A = a.patient_name?.toLowerCase() || "";
      const B = b.patient_name?.toLowerCase() || "";
      return sortOrder === "asc"
        ? A.localeCompare(B)
        : B.localeCompare(A);
    });

  // ✅ Reset page on search
  useEffect(() => {
    setCurrentPage(0);
  }, [searchText]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const paginatedData = filteredData.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage
  );

  const toggleSort = () => {
    setSortOrder((p) => (p === "asc" ? "desc" : "asc"));
  };

  const handlePageChange = (page) => {
    if (page >= 0 && page < totalPages) setCurrentPage(page);
  };

  return (
    <div className="card">
      <div className="card-body">
        {loading ? (
          <div className="text-center my-5">
            <Loader size="lg" />
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-striped mb-4">
                <thead className="table-light">
                  <tr>
                    <th style={{ cursor: "pointer" }} onClick={toggleSort}>
                      Patient Name {sortOrder === "asc" ? "↑" : "↓"}
                      <input
                        className="form-control form-control-sm mt-1"
                        placeholder="Search name"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>

                    <th>User</th>
                    <th>Google Review</th>
                    <th>Testimonial</th>
                    <th>Approve</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedData.map((item) => (
                    <tr key={item.review_id}>
                      <td>{item.patient_name || "-"}</td>

                      {/* ✅ USER */}
                      <td>{item.user_name || "-"}</td>

                      {/* GOOGLE REVIEW */}
                      <td>
                        <span
                          className={`badge ${
                            item.review_provided === "Yes"
                              ? "bg-success"
                              : "bg-danger"
                          }`}
                        >
                          {item.review_provided}
                        </span>
                      </td>

                      {/* TESTIMONIAL */}
                      <td>
                        <span
                          className={`badge ${
                            item.testimonial_provided === "Yes"
                              ? "bg-success"
                              : "bg-danger"
                          }`}
                        >
                          {item.testimonial_provided}
                        </span>
                      </td>

                      {/* APPROVE - FIXED CONDITION */}
                      <td>
                        {item.admin_approval?.toLowerCase() === "approved" ? (
                          <span className="badge bg-success">Approved</span>
                        ) : (
                          // Show approve button only when BOTH review and testimonial are "Yes"
                          item.review_provided?.toLowerCase() === "yes" && 
                          item.testimonial_provided?.toLowerCase() === "yes" && (
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleApprove(item)}
                            >
                              Approve
                            </button>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                  {paginatedData.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No reviews found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ✅ PAGINATION */}
            <div
              className="pagination-wrapper"
              style={{
                display: "flex",
                gap: "6px",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "20px",
                flexWrap: "wrap",
                flexDirection: "column",
              }}
            >
              <div>
                Showing {filteredData.length === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
                {Math.min(
                  (currentPage + 1) * rowsPerPage,
                  filteredData.length
                )}{" "}
                of {filteredData.length} entries
              </div>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                <button
                  onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                  disabled={currentPage === 0}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    color: "#000",
                    cursor: currentPage === 0 ? "not-allowed" : "pointer",
                    opacity: currentPage === 0 ? 0.5 : 1,
                  }}
                >
                  &lt;
                </button>
                
                {(() => {
                  const pages = [];
                  const maxPagesToShow = 5;
                  const startPage = Math.max(0, Math.min(currentPage - 2, totalPages - maxPagesToShow));
                  const endPage = Math.min(startPage + maxPagesToShow, totalPages);

                  if (startPage > 0) {
                    pages.push(
                      <button
                        key={0}
                        onClick={() => setCurrentPage(0)}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: "pointer",
                        }}
                      >
                        1
                      </button>
                    );
                    if (startPage > 1) {
                      pages.push(
                        <span
                          key="dots1"
                          style={{ padding: "6px 12px", userSelect: "none" }}
                        >
                          ...
                        </span>
                      );
                    }
                  }

                  for (let i = startPage; i < endPage; i++) {
                    const isActive = currentPage === i;
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: isActive ? "#007bff" : "#fff",
                          color: isActive ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                      >
                        {i + 1}
                      </button>
                    );
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(
                        <span
                          key="dots2"
                          style={{ padding: "6px 12px", userSelect: "none" }}
                        >
                          ...
                        </span>
                      );
                    }
                    pages.push(
                      <button
                        key={totalPages - 1}
                        onClick={() => setCurrentPage(totalPages - 1)}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: "pointer",
                        }}
                      >
                        {totalPages}
                      </button>
                    );
                  }
                  return pages;
                })()}

                <button
                  onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                  disabled={currentPage === totalPages - 1}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                    color: "#000",
                    cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                    opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                  }}
                >
                  &gt;
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PatientReviewList;